import React, {useMemo} from 'react'
import styled from 'styled-components'
import {TransactionStatus} from 'types'
import {getContentURL, getCurrencyValue} from 'utils'
import {Paragraph, Image} from 'common/components'
import convertUnit from 'lib/unit'
import {useWindowMode} from 'windows'
import {GiftShopTemplateContentVideoBadge} from '../../../../gift-shop'
import {CopyInvoice} from '../CopyInvoice'
import {TransactionDetailStatus} from '../Status'
import {TransactionDetailContentItemMultiCreatorProps} from './TransactionDetailContentItemMultiCreatorProps'

const StyledItemContainer = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: ${convertUnit(12)};
  cursor: pointer;
`

const StyledItem = styled.div`
  display: flex;
  flex: 2;
  flex-direction: column;
  justify-content: center;
  margin-left: ${convertUnit(5)};
`

const StyledGridPhoto = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  margin-right: ${convertUnit(15)};
  margin-top: ${convertUnit(4)};
`

const StyledItemEnd = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  align-self: start;
  gap: ${convertUnit(4)};
`

const StyledStrikeThroughParagraph = styled(Paragraph)`
  text-decoration: line-through;
`

const StyledImage = styled(Image)`
  width: ${convertUnit(64)};
  height: ${convertUnit(64)};
`

const StyledItemFirst = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`

const StyledContentCountOverlay = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: ${convertUnit(64)};
  height: ${convertUnit(64)};
  background: #00000066;
  border-radius: ${convertUnit(8)};
  display: flex;
`

export default function TransactionDetailContentItemMultiCreator({
  invoice,
  showDiscount,
  onClickItem,
  itemFooter,
}: TransactionDetailContentItemMultiCreatorProps) {
  const mode = useWindowMode()
  const {
    content = [],
    package: packageData = [],
    creator_name,
    invoice_status: status,
    invoice_id,
    price_detail,
  } = invoice

  const allItems = useMemo(() => [...content, ...packageData], [
    content,
    packageData,
  ])

  const {content_type, url, type, title} = content[0]

  return (
    <StyledItemContainer onClick={() => onClickItem()}>
      <StyledGridPhoto>
        {content_type === 'video' && (
          <GiftShopTemplateContentVideoBadge width={20} />
        )}
        <StyledImage
          useLazyLoad
          src={getContentURL({url, type})}
          alt={`content-item:${title}`}
          style={{borderRadius: convertUnit(8)}}
          width={64}
          height={64}
        />
        {allItems.length > 1 && (
          <StyledContentCountOverlay>
            <Paragraph
              color="white_1"
              fontWeight="bold"
              fontSize="l"
              style={{
                margin: 'auto',
              }}>
              {allItems.length}+
            </Paragraph>
          </StyledContentCountOverlay>
        )}
      </StyledGridPhoto>
      <StyledItem>
        <StyledItemFirst>
          <TransactionDetailStatus
            status={status as TransactionStatus}
            paragraphProps={{
              fontSize: 's',
            }}
          />
          <CopyInvoice invoice_id={invoice_id} />
        </StyledItemFirst>
        <Paragraph
          fontWeight="bold"
          color="black_1"
          fontSize="m"
          lineHeight={24}>
          {mode === 'mobile' && creator_name && creator_name.length > 15
            ? `${creator_name.slice(0, 15)}...`
            : creator_name}
        </Paragraph>
        <StyledItemEnd>
          {!showDiscount ||
          price_detail.final.nominal ===
            price_detail.final_without_discount.nominal ? (
            <Paragraph fontSize="s" fontWeight="bold" color="info_5">
              {getCurrencyValue(price_detail.final.nominal)}
            </Paragraph>
          ) : (
            <>
              <Paragraph fontSize="s" fontWeight="bold" color="info_5">
                {getCurrencyValue(price_detail.final.nominal)}
              </Paragraph>
              <StyledStrikeThroughParagraph fontSize="s" color="gray_3">
                {getCurrencyValue(price_detail.final_without_discount.nominal)}
              </StyledStrikeThroughParagraph>
            </>
          )}
        </StyledItemEnd>
        {itemFooter}
      </StyledItem>
    </StyledItemContainer>
  )
}
